/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt, presetRange } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import SleepSummaryDetailSummaryCards from './SleepSummaryDetailSummaryCards';
import SleepDurationTrendChart from './charts/SleepDurationTrendChart';
import SleepStageBreakdown from './charts/SleepStageBreakdown';
import '../active-users/active-users.css';
import './SleepSummary.css';

const EXPORT_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'total_duration_minutes', label: 'Total Duration (min)' },
  { key: 'awake_minutes', label: 'Awake (min)' }, { key: 'awake_pct', label: 'Awake (%)' },
  { key: 'rem_minutes', label: 'REM (min)' }, { key: 'rem_pct', label: 'REM (%)' },
  { key: 'light_minutes', label: 'Light (min)' }, { key: 'light_pct', label: 'Light (%)' },
  { key: 'deep_minutes', label: 'Deep (min)' }, { key: 'deep_pct', label: 'Deep (%)' },
  { key: 'asleep_minutes', label: 'Asleep (min)' }, { key: 'asleep_pct', label: 'Asleep (%)' },
];

const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const formatDuration = (minutes) => {
  const mins = Number(minutes) || 0;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
};

const SleepSummaryDetail = () => {
  const { encodedUserId } = useParams();
  const decodedUserId = encodedUserId ? atob(encodedUserId) : '';
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({});
  const [user, setUser] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) getChartData(fromDate, toDate);
  }, [token]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const getChartData = async (f, t) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/user-sleep-summary-details?user_id=${decodedUserId}&from_date=${f}&to_date=${t}`, auth());
      const data = res.data;
      setChartData(data.chart_data || []);
      setSummary(data.summary || {});
      setUser(data.user || {});
      if (data.chart_data && data.chart_data.length > 0) {
        const firstDate = data.chart_data[0].date; // most recent (ordered DESC)
        setSelectedDate(firstDate);
        fetchBreakdown(firstDate);
      } else {
        setSelectedDate(null); setBreakdown(null);
      }
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally { setLoading(false); }
  };

  const fetchBreakdown = useCallback(async (date) => {
    setBreakdownLoading(true);
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/user-sleep-stage-breakdown?user_id=${decodedUserId}&date=${date}`, auth());
      setBreakdown(res.data.breakdown || null);
    } catch (err) {
      setBreakdown(null);
    }
    setBreakdownLoading(false);
  }, [decodedUserId, token]);

  const handleBarClick = (date) => { setSelectedDate(date); fetchBreakdown(date); };

  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key) => {
    const { fromStr, toStr } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(`${fromStr}T00:00:00`) : null);
    setDisplayToDate(toStr ? new Date(`${toStr}T00:00:00`) : null);
    getChartData(fromStr, toStr);
  };
  const handleSearch = () => getChartData(fromDate, toDate);
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null); setPreset(null);
    getChartData('', '');
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/user-sleep-summary-detail-export?user_id=${decodedUserId}&from_date=${fromDate}&to_date=${toDate}`, auth());
    return res.data.data || [];
  };

  const userName = user.name || '';
  const hasRange = !!(fromDate || toDate);

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Sleep summary › <b>Details</b></span>
            <span className="au-route">/user-sleep-summary-details</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/user-sleep-summary">← Back</Link></span>
          </div>
          <h1 className="au-title">Sleep Summary Detail{userName ? <span className="au-subtitle"> — {userName}</span> : null}</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load this user’s sleep data. Please try again." onRetry={() => getChartData(fromDate, toDate)} />
          ) : (
            <>
              <SleepSummaryDetailSummaryCards
                totalSessions={summary.total_sessions || 0}
                avgSleepMinutes={summary.avg_sleep_minutes || 0}
              />

              <DateRangeBar
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate}
                hasRange={hasRange} onPreset={handlePreset} onFromChange={handleFromChange}
                onToChange={handleToChange} onSearch={handleSearch} onClear={clearFilter}
              />

              <p className="ss-range-note" data-testid="ss-detail-range-note">
                Filtered by the sleep date (session start, UTC).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all sleep dates (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={6} /></div>
              ) : chartData.length === 0 ? (
                <EmptyState
                  title="No sleep data in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'This user has no sleep records in the selected window.' : 'This user has no sleep records yet.'}
                />
              ) : (
                <>
                  <div className="au-panel ss-bar-panel">
                    <div className="au-panel__head">
                      <h4 className="au-panel__title">Sleep duration over time</h4>
                      <span className="au-spacer" />
                      <div className="au-legend"><span><i style={{ background: '#f26b45' }} />Sleep duration (hours/night)</span></div>
                    </div>
                    <SleepDurationTrendChart data={chartData} />
                  </div>

                  {selectedDate && (
                    <div className="au-panel">
                      <div className="au-panel__head">
                        <h4 className="au-panel__title">Sleep Stages - {selectedDate}</h4>
                      </div>
                      {breakdownLoading ? (
                        <div className="au-chart-empty">Loading…</div>
                      ) : (
                        <SleepStageBreakdown breakdown={breakdown} />
                      )}
                    </div>
                  )}

                  <div className="au-panel">
                    <div className="au-panel__head">
                      <h4 className="au-panel__title">Daily sleep</h4>
                      <span className="au-spacer" />
                      <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename={`sleep-detail-${decodedUserId}.csv`} />
                    </div>
                    <div className="au-table-wrap">
                      <table className="au-table ss-table" data-testid="ss-detail-table">
                        <thead>
                          <tr>
                            <th>No</th>
                            <th>Date</th>
                            <th>Total Duration</th>
                            <th>Sessions</th>
                            <th>Stages</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chartData.map((row, index) => (
                            <tr
                              key={row.date}
                              className={row.date === selectedDate ? 'ss-row--selected' : ''}
                            >
                              <td data-label="No">{index + 1}</td>
                              <td data-label="Date">{dmy(row.date)}</td>
                              <td data-label="Total Duration" className="tnum">{formatDuration(row.total_sleep_minutes)}</td>
                              <td data-label="Sessions" className="tnum">{row.uuid_count}</td>
                              <td data-label="Stages">
                                <button
                                  type="button"
                                  className="au-link ss-link-btn"
                                  onClick={() => handleBarClick(row.date)}
                                  aria-label={`View sleep stages for ${dmy(row.date)}`}
                                >
                                  {row.date === selectedDate ? 'Viewing' : 'View stages'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SleepSummaryDetail;
